import { type TDeviceType } from '@cian/ui-kit';

import { type FC, useMemo } from 'react';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { type TAdfoxBannerCompleteSizes } from 'shared/types/adfox';
import { Ticker } from 'shared/components/Ticker';
import { type TParallaxGeneralContext } from 'shared/types/bannerContext';

import { BACKGROUND_MAX_HEIGHT } from './internal/constants';
import { getBackgroundImageUrl } from './internal/utils/getBackgroundImageUrl';
import { getTextImageUrl } from './internal/utils/getImageUrl';
import { useParallax } from './internal/hooks/useParallax';

import styles from './ParallaxGeneral.css';

export type TParallaxGeneralProps = {
  context: TParallaxGeneralContext;
  sizes: TAdfoxBannerCompleteSizes;
  deviceType: TDeviceType;
};

export const ParallaxGeneral: FC<TParallaxGeneralProps> = ({ context, sizes, deviceType }) => {
  const { annotation, url, label, mortgageWarning, mortgageTermsWarning } = context;

  const imageUrls = useMemo(
    () => ({
      backgroundImage: getBackgroundImageUrl(context, deviceType),
      textImage: getTextImageUrl(context, deviceType),
    }),
    [context, deviceType],
  );

  const { containerRef, animatedBgRef } = useParallax();

  return (
    <a
      className={styles['link-container']}
      data-testid="parallax-general"
      href={url}
      rel="noreferrer noopener nofollow"
      style={sizes}
      target="_blank"
    >
      <div className={styles['container']} ref={containerRef}>
        <div
          className={styles['background']}
          ref={animatedBgRef}
          style={{ backgroundImage: imageUrls.backgroundImage, height: BACKGROUND_MAX_HEIGHT }}
        />
        <div className={styles['image']} style={{ backgroundImage: imageUrls.textImage }} />
        {annotation && (
          <div className={styles['annotation']}>
            <Ticker text={annotation} theme="dark-background" />
          </div>
        )}
        {!!label && (
          <div className={styles['label-wrapper']}>
            <AdLabel label={label} />
          </div>
        )}
      </div>
      {(mortgageWarning || mortgageTermsWarning) && (
        <div className={styles['mortgage-annotations-wrapper']}>
          <MortgageAnnotations
            height={sizes.height}
            mortgageTermsWarning={mortgageTermsWarning}
            mortgageWarning={mortgageWarning}
            splitMortgageTermsWarning={deviceType === 'desktop' ? 'auto' : 'force'}
          />
        </div>
      )}
    </a>
  );
};
